import type { MapImages } from "./drawMatchMap";
import type { MapColors } from "./MapColors";
import type { MapScene, SceneCamp } from "./MapScene";
import {
  bossMark,
  buffMark,
  campMark,
  deathMark,
  objectiveMark,
  playerMark,
  runeMark,
  shopMark,
  trailSegment,
  ZIPLINE_WIDTH,
} from "./marks";

// Образцы легенды: каждый рисуется теми же отметками, что и карта.
export type LegendSwatch =
  | "me"
  | "heat"
  | "myDeath"
  | "myKill"
  | "mate"
  | "enemy"
  | "mateDeath"
  | "enemyDeath"
  | "campMe"
  | "campTeam"
  | "campMissed"
  | "campEnemy"
  | "guardian"
  | "walker"
  | "patron"
  | "destroyed"
  | "rune"
  | "boss"
  | "buff"
  | "shop"
  | "zipline";

// Сторона образца в единицах сцены: в неё помещаются крупный герой и мид-босс.
const SWATCH_UNITS = 60;
const CAMP_SCALE = 0.9;

const CAMP_STATUS: Partial<Record<LegendSwatch, SceneCamp["status"]>> = {
  campMe: "me",
  campTeam: "team",
  campMissed: "missed",
  campEnemy: "enemy",
};

// Иконка лагеря в цвете статуса: средний лагерь, а нет его на карте — любой.
const CAMP_KINDS: readonly SceneCamp["kind"][] = ["medium", "weak", "strong", "vault"];
const campIcon = (
  images: Pick<MapImages, "camps">,
  status: SceneCamp["status"],
): { kind: SceneCamp["kind"]; icon: CanvasImageSource | undefined } => {
  for (const kind of CAMP_KINDS) {
    const icon = images.camps.get(`${kind}:${status}`);
    if (icon !== undefined) {
      return { kind, icon };
    }
  }
  return { kind: "medium", icon: undefined };
};

// Рисует образец `swatch` на canvas `size`×`size` CSS-пикселей.
export const drawLegendSwatch = (
  context: CanvasRenderingContext2D,
  swatch: LegendSwatch,
  { size, dpr }: { size: number; dpr: number },
  scene: MapScene,
  images: Pick<MapImages, "camps" | "heroes">,
  colors: MapColors,
): void => {
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, size * dpr, size * dpr);
  const scale = (size / SWATCH_UNITS) * dpr;
  context.setTransform(scale, 0, 0, scale, (size * dpr) / 2, (size * dpr) / 2);
  const own = colors.teams[scene.team] ?? colors.mate;
  const enemy = colors.teams[1 - scene.team] ?? colors.mate;
  const heroOf = (ours: boolean) =>
    scene.others.find((player) => (player.team === scene.team) === ours)?.heroId;
  const face = (heroId: number | undefined) =>
    heroId === undefined ? undefined : images.heroes.get(heroId);

  const status = CAMP_STATUS[swatch];
  if (status !== undefined) {
    const { kind, icon } = campIcon(images, status);
    // Подсветка свободного лагеря шире образца — чуть уменьшаем все лагеря одинаково.
    context.scale(CAMP_SCALE, CAMP_SCALE);
    campMark(context, false, { x: 0, y: 0, kind, status }, icon, colors);
    return;
  }
  switch (swatch) {
    case "me":
      for (let k = 0; k < 12; k += 1) {
        trailSegment(
          context,
          [-28 + k * 2.4, 0],
          [-28 + (k + 1) * 2.4, 0],
          k / 12,
          ((k + 1) / 12) ** 1.4,
          colors,
        );
      }
      playerMark(context, false, 6, 0, "me", own, face(scene.me.heroId), colors);
      return;
    case "heat": {
      // Цвета тепла (`HeatGrid.paint`): от слабого к самому долгому.
      const gradient = context.createLinearGradient(-24, 0, 24, 0);
      gradient.addColorStop(0, "rgb(255 128 30 / 0.3)");
      gradient.addColorStop(0.55, "rgb(255 190 30 / 0.65)");
      gradient.addColorStop(1, "rgb(255 245 160 / 0.9)");
      context.fillStyle = gradient;
      context.beginPath();
      context.roundRect(-24, -9, 48, 18, 9);
      context.fill();
      return;
    }
    case "myDeath":
      deathMark(context, 0, 0, "me", colors.missed, colors);
      return;
    case "myKill":
      deathMark(context, 0, 0, "kill", colors.kill, colors);
      return;
    case "mateDeath":
      deathMark(context, 0, 0, "other", own, colors);
      return;
    case "enemyDeath":
      deathMark(context, 0, 0, "other", enemy, colors);
      return;
    case "mate":
      playerMark(context, false, 0, 0, "other", own, face(heroOf(true)), colors);
      return;
    case "enemy":
      playerMark(context, false, 0, 0, "other", enemy, face(heroOf(false)), colors);
      return;
    case "guardian":
    case "walker":
    case "patron":
      objectiveMark(context, 0, 0, swatch, own, colors);
      return;
    case "destroyed": {
      // Разрушенное здание: на миникарте остаётся только контур.
      const r = 26 / Math.SQRT2;
      context.strokeStyle = colors.building;
      context.lineWidth = 4;
      context.beginPath();
      context.moveTo(0, -r);
      context.lineTo(r, 0);
      context.lineTo(0, r);
      context.lineTo(-r, 0);
      context.closePath();
      context.stroke();
      return;
    }
    case "rune":
      runeMark(context, 0, 0, colors);
      return;
    case "boss":
      bossMark(context, 0, 0, true, colors);
      return;
    case "buff":
      buffMark(context, 0, 0, enemy, colors);
      return;
    case "shop":
      shopMark(context, 0, 0, colors);
      return;
    case "zipline":
      context.lineWidth = ZIPLINE_WIDTH;
      context.lineCap = "round";
      context.strokeStyle = enemy;
      context.beginPath();
      context.moveTo(-26, -14);
      context.quadraticCurveTo(-6, -10, 0, 0);
      context.stroke();
      context.strokeStyle = own;
      context.beginPath();
      context.moveTo(0, 0);
      context.quadraticCurveTo(6, 10, 26, 14);
      context.stroke();
      return;
    default:
      return;
  }
};
